import { ErrorMessage } from '@atlaskit/form';
import AddIcon from '@atlaskit/icon/core/add';
import Page16Icon from '@atlaskit/icon-object/glyph/page/16';
import { Box } from '@atlaskit/primitives';
import {
  AsyncSelect,
  type FormatOptionLabelMeta,
  type SelectProps,
} from '@databases/atlaskit/Select';
import { searchContent } from '@databases/connections/content/contentSearch';
import { type SearchContent } from '@databases/connections/types';
import { createContentARI } from '@databases/connections/utils/ariHelper';
import ContentLink from '@databases/content-link/ContentLink';
import { getCloudId } from '@databases/database-state';
import { messages } from '@databases/intl/messages';
import { defaultStoreUtils } from '@databases/redux';
import {
  type ContentReference,
  type ResolvedContentReference,
  type CreatePageReference,
  type Content,
} from '@databases/types/confluence';
import { DatabaseFieldType, PageSubType } from '@databases/types/database';
import { getPageKey, pageToReference } from '@databases/utils/confluence/utils';
import {
  type MultiChange,
  MultiValueChangeType,
  type ValueChange,
} from '@databases/utils/database/changesets';
import { stringToDate, toDisplayDate } from '@databases/utils/database/field-types/date';
import { loadContentById, loadContentRef } from '@databases/utils/redux/content/actions';
import { selectBaseUrl } from '@databases/utils/redux/context/selectors';
import { matchViewPageOrBlogpostPath } from '@databases/utils/urls';
import { generateId } from '@databases/utils/utils';
import debounce from 'debounce-promise';
import React, {
  type FunctionComponent,
  type ReactNode,
  useCallback,
  useMemo,
} from 'react';
import { useIntl } from 'react-intl-next';

import {
  CustomValueContainer,
  baseSelectStyleConfig,
  isRenderedInSidePanelStyling,
} from '../popover-edit-common';

import { OptionContainer } from './pagePicker.styles';
import { type ExtendedPageReference, type Value } from './types';

type Option = CreatePageReference | ExtendedPageReference | ContentReference;

// deleted/no permissions pages only have content ID, but the create page object
// does not have a content ID so we use the space key + content title for that
const getOptionValue = (value: Option) =>
  'contentId' in value ? value.contentId : getPageKey(value);

export function isPageReference(value: Option): value is ExtendedPageReference {
  return 'contentTitle' in value;
}

const isCreateOption = (value: Option): value is CreatePageReference => 'create' in value;

export interface MultiPagePickerProps {
  onChange: (value: Option) => void;
  onFocus?: (e: React.FocusEvent<HTMLElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLElement>) => void;
  autoFocus?: boolean;
  isPending?: boolean;
  isMulti?: boolean;
  isSeamless?: boolean;
  isInvalid?: boolean;

  onInputChange?: SelectProps<Option, boolean>['onInputChange'];
  preserveInputValue?: boolean;

  /**
   * If this is true, a virtual option will be shown above all pages to create a page with the name that the user
   * has typed in (if no page with the same title is in the list). The virtual option will be of type
   * OptionalCreatePageReferenceOrContentId and have its "create" property set
   * with the parent page configured in the creationParentPage prop.
   */
  allowCreation?: boolean;
  /**
   * If creation is allowed, this is the parent page under which the new page
   * will be created. If this is undefined, the creation option will be disabled.
   */
  creationParentPage?: ResolvedContentReference;
  /**
   * The error message to show when creation is allowed but there is no valid
   * parent page.
   */
  creationErrorMessage?: string;

  pageSubType?: PageSubType;

  isLoading?: boolean;

  isLiveDocEnabled?: boolean;
  isRenderedInSidePanel?: boolean;
}

export const processSearchContentData = (
  data: SearchContent[]
): ExtendedPageReference[] => {
  return data.map(
    (result) =>
      ({
        type: result.content.type,
        spaceKey: result.container.displayUrl.split('/spaces/')[1],
        contentTitle: result.title,
        contentId: result.content.id,
        ari: createContentARI(result.content.type, result.content.id),
        metadata: {
          lastModified: result.lastModified,
          spaceName: result.container.title,
        },
      } as ExtendedPageReference)
  );
};

/**
 * An asynchronous select box that allows searching for pages within the Confluence instance. By default it shows
 * the last viewed pages, but when the user types in a search term, it searches for pages whose title contains that
 * term.
 */
const MultiPagePicker: FunctionComponent<MultiPagePickerProps> = ({
  onChange,
  onFocus,
  onBlur,
  autoFocus,
  creationParentPage,
  allowCreation = true,
  creationErrorMessage,
  isPending,
  isLoading,
  pageSubType,
  isLiveDocEnabled,
  isRenderedInSidePanel,
}) => {
  const { formatMessage } = useIntl();

  const baseUrl = defaultStoreUtils.useSelector(selectBaseUrl);
  const handleChange = useCallback(
    (val: Option | null) => {
      if (!val) {
        return;
      }
      onChange(val);
    },
    [onChange]
  );

  const formatOptionLabel = useCallback(
    (value: Option, meta: FormatOptionLabelMeta<Option>): ReactNode => {
      if (meta.context === 'value') {
        // selected value will never be CreatePageReference
        if (isCreateOption(value)) {
          return;
        }
        return <ContentLink contentId={value.contentId} noFocus />;
      } else {
        // if a page is unable to be resolved, we can't show it in the menu
        if (!isPageReference(value) && !isCreateOption(value)) {
          return;
        }
        const selectedValues: Array<string> = (
          Array.isArray(meta.selectValue)
            ? meta.selectValue
            : meta.selectValue
            ? [meta.selectValue]
            : []
        ).map((val: Option) => getOptionValue(val));

        const isSelected = selectedValues.includes(getOptionValue(value));
        const title = value.contentTitle;

        if (isCreateOption(value)) {
          return (
            <OptionContainer
              data-testid="page-link-option-container"
              isLoading={isLoading}
              isSelected={isSelected}
            >
              <AddIcon spacing="spacious" label="" />
              <span data-testid="create-page-message">
                {isLiveDocEnabled && pageSubType === PageSubType.LIVE
                  ? formatMessage(
                      messages['field-settings.type-page-link-create-live-doc-text'],
                      { title }
                    )
                  : formatMessage(
                      messages['field-settings.type-page-link-create-page-text'],
                      { title }
                    )}
              </span>
              <span>
                {creationParentPage ? (
                  formatMessage(
                    messages['field-settings.type-page-link-create-under-text'],
                    {
                      page: creationParentPage.contentTitle,
                    }
                  )
                ) : (
                  <ErrorMessage>
                    <Box paddingBlockStart="space.025" paddingInlineStart="space.025">
                      {creationErrorMessage ??
                        formatMessage(
                          messages['field-settings.type-page-link-create-inline-error']
                        )}
                    </Box>
                  </ErrorMessage>
                )}
              </span>
            </OptionContainer>
          );
        } else {
          const spaceName = value.metadata?.spaceName;
          const lastModified = value.metadata?.lastModified
            ? ` • ${formatMessage(
                messages['generic.page-picker.menu.option.description.last-updated'],
                {
                  date: toDisplayDate(
                    // For consistency with confluence, we do want to show the
                    // date in the user's local timezone
                    stringToDate(value.metadata?.lastModified, true) ??
                      new Date().toISOString()
                  ),
                }
              )}`
            : '';
          const description = `${spaceName ?? ''}${lastModified}`;

          return (
            <OptionContainer
              data-testid="page-link-option-container"
              isLoading={isLoading}
              isSelected={isSelected}
            >
              <Page16Icon label="" />
              <span>{title}</span>
              <span>{description}</span>
            </OptionContainer>
          );
        }
      }
    },
    [
      creationErrorMessage,
      creationParentPage,
      formatMessage,
      isLiveDocEnabled,
      isLoading,
      pageSubType,
    ]
  );

  const dispatch = defaultStoreUtils.useDispatch();

  const getContentBySearchQuery = useCallback(
    async (query: string): Promise<ExtendedPageReference[]> => {
      const cloudId = getCloudId();
      if (!cloudId) {
        return [];
      }
      const data = await searchContent(query, cloudId);

      return processSearchContentData(data);
    },
    []
  );

  const recentlyViewedContent = useMemo(async (): Promise<ExtendedPageReference[]> => {
    const cloudId = getCloudId();
    if (!cloudId) {
      return [];
    }
    const data = await searchContent('', cloudId);

    return processSearchContentData(data);
  }, []);

  const loadOptions = useCallback(
    async (inputValue: string) => {
      const inputValueTrimmed = inputValue.trim();
      const createPage =
        allowCreation && inputValueTrimmed
          ? ({
              type: 'page' as const,
              contentTitle: inputValueTrimmed,
              spaceKey: creationParentPage?.spaceKey ?? '',
              create: {
                parentPageId: creationParentPage?.contentId,
              },
            } as CreatePageReference)
          : undefined;

      const contentMatchFromUrl = inputValueTrimmed.startsWith(baseUrl)
        ? matchViewPageOrBlogpostPath(inputValueTrimmed)
        : undefined;

      const [
        recentlyViewedPages,
        searchResults,
        createPageResolved,
        resolvedContentMatchFromUrl,
      ] = await Promise.all([
        inputValueTrimmed ? [] : recentlyViewedContent,
        inputValueTrimmed ? getContentBySearchQuery(inputValueTrimmed) : [],

        // see if the page with that title already exists, load it in if so
        createPage && !contentMatchFromUrl
          ? dispatch(loadContentRef(createPage))
          : undefined,

        contentMatchFromUrl
          ? dispatch(loadContentById(contentMatchFromUrl.contentId))
          : undefined,
      ]);

      // resolvedContentMatchFromUrl is type Content<page>, we want to convert
      // it to ExtendedPageReference
      const pageMatchResolved =
        resolvedContentMatchFromUrl && resolvedContentMatchFromUrl.type === 'page'
          ? {
              ...pageToReference(resolvedContentMatchFromUrl),
              metadata: {
                lastModified: resolvedContentMatchFromUrl.history?.lastUpdated?.when,
                spaceName: resolvedContentMatchFromUrl.space?.name,
              },
            }
          : undefined;

      const results = inputValueTrimmed ? searchResults : recentlyViewedPages;

      return [
        ...(pageMatchResolved ? [pageMatchResolved] : []),
        ...(!contentMatchFromUrl && createPage && !createPageResolved
          ? [createPage]
          : []),
        ...(!contentMatchFromUrl ? results : []),
      ];
    },
    [
      allowCreation,
      baseUrl,
      creationParentPage,
      dispatch,
      getContentBySearchQuery,
      recentlyViewedContent,
    ]
  );

  const debouncedLoadOptions = useMemo(() => debounce(loadOptions, 300), [loadOptions]);

  return (
    <AsyncSelect<Option>
      onChange={handleChange}
      loadOptions={debouncedLoadOptions}
      getOptionValue={getOptionValue}
      onFocus={onFocus}
      onBlur={onBlur}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      formatOptionLabel={formatOptionLabel}
      placeholder={
        allowCreation
          ? formatMessage(messages['page-link-picker-popover.search-create-text'])
          : formatMessage(messages['page-link-picker-popover.search-text'])
      }
      loadingMessage={() =>
        formatMessage(messages['field-settings.select-picker-loading-text'])
      }
      autoFocus={autoFocus}
      isLoading={isLoading || isPending}
      styles={
        isRenderedInSidePanel ? isRenderedInSidePanelStyling : baseSelectStyleConfig
      }
      isOptionDisabled={(option: Option) => isCreateOption(option) && !creationParentPage}
      controlShouldRenderValue={false}
      inputId="page-picker-input"
      {...(!isRenderedInSidePanel && {
        menuPortalTarget: null,
        defaultMenuIsOpen: true,
        menuIsOpen: true,
        components: {
          IndicatorsContainer: () => null,
          ValueContainer: CustomValueContainer,
        },
      })}
      classNamePrefix="page-link-select"
    />
  );
};

export interface PageLinkSelectProps
  extends Omit<MultiPagePickerProps, 'value' | 'onChange'> {
  onChange: (value: Value[], change: ValueChange | null) => void;
  createNewPage: (value: Value, index: number) => Promise<Content | undefined>;
  getAriForPageContentId: (contentId: string) => string;
  resolvedValues: Value[];
}

const PageLinkSelect: FunctionComponent<PageLinkSelectProps> = ({
  onChange,
  createNewPage,
  getAriForPageContentId,
  resolvedValues,
  isMulti,
  ...props
}) => {
  const onItemAdd = useCallback(
    async (value: Value) => {
      const newPage = await createNewPage(value, resolvedValues.indexOf(value));
      if (newPage) {
        value = {
          _id: value._id,
          ari: getAriForPageContentId(newPage.id),
          contentId: newPage.id,
        };
      }

      const pageValues = [...resolvedValues, value];

      const delta: MultiChange<DatabaseFieldType.PAGE_LINK> = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: value,
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [value],
          };

      onChange(pageValues, {
        type: DatabaseFieldType.PAGE_LINK,
        delta,
      });
    },
    [createNewPage, getAriForPageContentId, isMulti, onChange, resolvedValues]
  );

  const handleChange = useCallback(
    (value: Option) => {
      if (!value) {
        return;
      }
      const valToAdd = {
        ...value,
        _id: generateId(),
        contentId: 'contentId' in value ? value.contentId : '',
        ari: 'ari' in value ? value.ari : '',
      };
      onItemAdd(valToAdd);
    },
    [onItemAdd]
  );

  return (
    <MultiPagePicker {...props} onChange={handleChange} isLoading={props.isLoading} />
  );
};

export default PageLinkSelect;
